import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { NAV, canSeeNavItem } from '../navConfig';
import ModuleCard from '../components/ModuleCard';
import { PERM, can } from '../utils';

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true });
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const date = now.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <div style={{
      position: 'relative', display: 'flex', alignItems: 'center', gap: 14,
      padding: '12px 18px 12px 12px', borderRadius: 14,
      background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.28)',
      color: '#fff',
    }}>
      <span style={{
        width: 44, height: 44, borderRadius: 12, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(34,211,238,0.14)', border: '1.5px solid rgba(34,211,238,0.45)', color: '#67e8f9',
      }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
        </svg>
      </span>
      <span>
        <span style={{ display: 'block', fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
          {time.replace(/\s?(am|pm)$/i, '')}
          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', marginLeft: 3 }}>:{seconds}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#67e8f9', marginLeft: 6 }}>{now.getHours() < 12 ? 'AM' : 'PM'}</span>
        </span>
        <span style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.6)', marginTop: 4, textTransform: 'uppercase' }}>
          {date}
        </span>
      </span>
    </div>
  );
}

function WelcomeBanner({ user }) {
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'there';

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 18,
      padding: '24px 28px', marginBottom: 28, borderRadius: 18,
      background: 'linear-gradient(120deg, #031540 0%, #071e53 55%, #0b2a6f 100%)',
      border: '2px solid #0b2a6f',
      boxShadow: '0 4px 8px rgba(15,23,42,0.10), 0 22px 44px -16px rgba(3,21,64,0.65)',
    }}>
      {/* soft glows + fine grid, echoing the sidebar */}
      <div style={{ position: 'absolute', top: -140, left: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,211,238,0.22), transparent 65%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -160, right: 120, width: 360, height: 360, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.22), transparent 65%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)', backgroundSize: '28px 28px', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, #22d3ee, transparent)', opacity: 0.7 }} />

      <div style={{ position: 'relative', minWidth: 0 }}>
        <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.2em', color: '#67e8f9', marginBottom: 8 }}>
          RIFITEL NIGERIA LIMITED · BUSINESS CENTRAL
        </div>
        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', lineHeight: 1.2 }}>
          {greeting}, {name} <span role="img" aria-label="wave">👋</span>
        </div>
        <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.68)', marginTop: 8 }}>
          Choose a module below to get started
        </div>
      </div>

      <LiveClock />
    </div>
  );
}

export default function DashboardPage({ user }) {
  const navigate = useNavigate();

  // One flat grid in sidebar order, so My Profile stays the last card.
  const modules = NAV
    .flatMap(g => g.items)
    .filter(item => item.id !== 'dashboard' && canSeeNavItem(user, item));

  // Without the Dashboard view permission, land on the first module this person can open.
  if (user && !can(user, PERM.DASHBOARD_VIEW)) {
    const first = modules.find(m => !m.comingSoon);
    return <Navigate to={first ? `/${first.id}` : '/profile'} replace />;
  }

  return (
    <div className="fade-in">
      <WelcomeBanner user={user} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 18 }}>
        {modules.map(item => (
          <ModuleCard key={item.id} item={item} onOpen={() => navigate(`/${item.id}`)} />
        ))}
      </div>
    </div>
  );
}
